import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const ClientSearchProviders = ({ onRequest }) => {
  const [role, setRole] = useState('');
  const [query, setQuery] = useState('');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  // Real-time search with debounce
  useEffect(() => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    const timeout = setTimeout(() => {
      if (query || role) searchProviders();
      else setProviders([]);
    }, 300); // 300ms debounce for smoother UX
    setDebounceTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [query, role]);

  const searchProviders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}oosh/service/search-providers`, {
        params: { role, query },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setProviders(res.data);
    } catch (err) {
      toast.error('Search failed');
      console.error(err);
    }
    setLoading(false);
  };

  const requestService = async (providerId, providerModel, serviceName) => {
    const requestDetails = prompt('Enter service request details:');
    if (!requestDetails) return;
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}oosh/service/request-service`,
        {
          providerId,
          providerModel,
          roleRequested: role || serviceName.split(' ')[0].toLowerCase(),
          serviceName,
          requestDetails,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Service request sent');
      onRequest(res.data); // Pass the new service to parent (e.g., to refresh dashboard)
    } catch (err) {
      toast.error('Request failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white shadow-md rounded-lg">
      <h2 className="text-2xl font-bold mb-6 text-blue-600">Find Service Providers</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Roles</option>
          <option value="contractor">Contractor</option>
          <option value="serviceprovider">Service Provider</option>
          <option value="supplier">Supplier</option>
        </select>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, service, about, bio, or past jobs..."
          className="p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={searchProviders}
          className="bg-blue-500 text-white p-2 rounded-md hover:bg-blue-600"
        >
          Search
        </button>
      </div>
      {loading && <p className="text-gray-500 text-center">Loading providers...</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {providers.map((provider) => (
          <div key={provider._id} className="border p-4 rounded-lg shadow-sm hover:shadow-md transition">
            <h3 className="font-bold text-lg mb-2">
              {provider.servicesRendered[0]?.companyName || `${provider.firstName || ''} ${provider.lastName || ''}`}
            </h3>
            <p className="text-sm mb-1"><strong>Services:</strong> {provider.servicesRendered.map((s) => s.serviceName).join(', ')}</p>
            <p className="text-sm mb-1"><strong>About:</strong> {provider.servicesRendered[0]?.aboutUs || 'N/A'}</p>
            <p className="text-sm mb-1"><strong>Bio:</strong> {provider.servicesRendered[0]?.bioData || 'N/A'}</p>
            <p className="text-sm mb-1"><strong>Experience:</strong> {provider.servicesRendered[0]?.yearsExperience || 'N/A'} years</p>
            <p className="text-sm mb-1"><strong>Home Service:</strong> {provider.servicesRendered[0]?.homeService ? 'Yes' : 'No'}</p>
            <p className="text-sm mb-1"><strong>Rate:</strong> ${provider.servicesRendered[0]?.amountCharged} / {provider.servicesRendered[0]?.duration}</p>
            <p className="text-sm mb-1"><strong>Location:</strong> {provider.servicesRendered[0]?.serviceCenter || 'N/A'}</p>
            <div className="mt-2">
              <h4 className="font-semibold text-sm mb-1">Past Reviews:</h4>
              {provider.serviceHistoryAsProvider.length > 0 ? (
                provider.serviceHistoryAsProvider.map((review) => (
                  <div key={review._id} className="ml-2 text-xs mb-2 border-l-2 pl-2 border-gray-200">
                    <p><strong>Comment:</strong> {review.comment}</p>
                    <p><strong>Job:</strong> {review.jobDescription}</p>
                    <p><strong>Rating:</strong> {review.rating}/5</p>
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500">No reviews yet</p>
              )}
            </div>
            <button
              onClick={() => requestService(provider._id, provider.model, provider.servicesRendered[0]?.serviceName || '')}
              className="mt-4 w-full bg-green-500 text-white p-2 rounded-md hover:bg-green-600"
            >
              Request Service
            </button>
          </div>
        ))}
      </div>
      {!loading && providers.length === 0 && (
        <p className="text-gray-500 text-center mt-6">No providers found</p>
      )}
    </div>
  );
};

export default ClientSearchProviders;